import {
  CidrMask,
  isPathLookupMaskValid,
  isUnicastHostModeValid,
} from '@ipfabric/utils';
import { getIpType } from '@ipfabric/utils/ipUtils';
import type { UnicastFormValues } from 'components/Graph/GraphSidePanel/GraphPathlookup/UnicastFormValues';
import { getIpVersion } from 'components/Graph/GraphSidePanel/GraphPathlookup/helpers/getIpVersion';
import type { FormikErrors } from 'formik';
import type { AppTranslations } from 'translations/translations';
import { isValidIpv4or6Address } from 'utils/formValidation/validations';
import { isMulticastAddress } from './helpers/isMulticastAddress';
import { validateEntryPoints } from './helpers/validateEntryPoints';
import { validateAdvancedOptions } from './validateAdvancedOptions';

export const validateUnicast =
  (msg: AppTranslations) =>
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: reasonable for validation
  (values: UnicastFormValues): FormikErrors<UnicastFormValues> => {
    const errors: FormikErrors<UnicastFormValues> = {};

    if (!values.startingPoint || !isValidIpv4or6Address(values.startingPoint)) {
      errors.startingPoint = 'Invalid IPv4 or IPv6 address or range';
    }

    if (isMulticastAddress(values.startingPoint)) {
      errors.startingPoint =
        msg.canvasGraph.graphSidePanel.pathlookup.validation.multicastSource;
    }

    if (
      !values.destinationPoint ||
      !isValidIpv4or6Address(values.destinationPoint)
    ) {
      errors.destinationPoint = 'Invalid IPv4 or IPv6 address or range';
    } else if (values.startingPoint === values.destinationPoint) {
      errors.destinationPoint =
        msg.canvasGraph.graphSidePanel.pathlookup.validation.notSameAsStartingPoint;
    }

    const startingPointVersion = getIpVersion(values.startingPoint);
    if (startingPointVersion !== null) {
      const destinationPointVersion = getIpVersion(values.destinationPoint);

      if (
        destinationPointVersion !== null &&
        destinationPointVersion !== startingPointVersion
      ) {
        errors.destinationPoint =
          'Cannot combine v4 and v6 addresses in one request';
      }
    }

    if (
      values.hostMode === true &&
      !errors.startingPoint &&
      !errors.destinationPoint &&
      !isUnicastHostModeValid(values.startingPoint, values.destinationPoint)
    ) {
      errors.hostMode =
        msg.canvasGraph.graphSidePanel.pathlookup.validation.hostModeOver255;
    }

    if (values.hostMode === false) {
      if (
        !errors.startingPoint &&
        !isPathLookupMaskValid(
          values.startingPoint,
          CidrMask.networkMode[getIpType(values.startingPoint)],
        )
      ) {
        errors.startingPoint =
          msg.canvasGraph.graphSidePanel.pathlookup.validation.shortMask(
            CidrMask.networkMode[getIpType(values.startingPoint)],
          );
      }

      if (
        !errors.destinationPoint &&
        !isPathLookupMaskValid(
          values.destinationPoint,
          CidrMask.networkMode[getIpType(values.destinationPoint)],
        )
      ) {
        errors.destinationPoint =
          msg.canvasGraph.graphSidePanel.pathlookup.validation.shortMask(
            CidrMask.networkMode[getIpType(values.destinationPoint)],
          );
      }
    }

    const entryPointsErrors = validateEntryPoints(values, msg);
    if (entryPointsErrors) {
      errors.entryPoints = entryPointsErrors;
    }

    const srcIpType = getIpType(values.startingPoint);

    if (values.protocol === 'icmp' && srcIpType !== 'ip') {
      errors.protocol =
        msg.canvasGraph.graphSidePanel.pathlookup.validation.icmpv4IpTypeMismatch;
    }

    if (values.protocol === 'icmpv6' && srcIpType !== 'ipv6') {
      errors.protocol =
        msg.canvasGraph.graphSidePanel.pathlookup.validation.icmpv6IpTypeMismatch;
    }

    return {
      ...errors,
      ...validateAdvancedOptions(msg, values),
    };
  };
